// Tab Simulazione del dettaglio preventivo PV: KPI, grafici e dettagli produzione/economici/ambientali
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { TabsContent } from '../ui/tabs';
import {
    MonthlyProductionChart,
    EnergyFlowsChart,
    AmortizationChart,
    EnergyCostComparisonChart,
} from '../QuoteSimulationCharts';
import { Sun, CheckCircle, CurrencyEur, TrendUp, Leaf, TreeEvergreen, Calculator } from '@phosphor-icons/react';
import { formatCurrency } from '../../lib/format';

export const QuoteSimulationTab = ({ quote, saving, onCalculate }) => (
    <TabsContent value="simulation" className="space-y-4">
        {quote.simulation ? (
            <div className="space-y-6">
                {/* Summary Cards Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                        <CardContent className="p-4 text-center">
                            <Sun className="w-8 h-8 mx-auto text-amber-600 mb-2" />
                            <p className="text-sm text-amber-700">Produzione Annua</p>
                            <p className="text-2xl font-bold text-amber-900">
                                {quote.simulation.annual_production_kwh?.toLocaleString()} kWh
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                        <CardContent className="p-4 text-center">
                            <CheckCircle className="w-8 h-8 mx-auto text-green-600 mb-2" />
                            <p className="text-sm text-green-700">Autoconsumo</p>
                            <p className="text-2xl font-bold text-green-900">
                                {quote.simulation.self_consumption_percent}%
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                        <CardContent className="p-4 text-center">
                            <CurrencyEur className="w-8 h-8 mx-auto text-blue-600 mb-2" />
                            <p className="text-sm text-blue-700">Risparmio Annuo</p>
                            <p className="text-2xl font-bold text-blue-900">
                                {formatCurrency(quote.simulation.annual_saving)}
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                        <CardContent className="p-4 text-center">
                            <TrendUp className="w-8 h-8 mx-auto text-purple-600 mb-2" />
                            <p className="text-sm text-purple-700">Break-even</p>
                            <p className="text-2xl font-bold text-purple-900">
                                {quote.simulation.break_even_year || '-'} anni
                            </p>
                        </CardContent>
                    </Card>
                </div>

                {/* Charts Row 1 */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <MonthlyProductionChart 
                        monthlyProduction={quote.simulation.monthly_production}
                        annualConsumption={quote.plant_data?.consumo_annuo_kwh}
                    />
                    <EnergyFlowsChart simulation={quote.simulation} />
                </div>

                {/* Charts Row 2 */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <AmortizationChart 
                        totalInvestment={quote.total_gross}
                        annualSaving={quote.simulation.annual_saving}
                    />
                    <EnergyCostComparisonChart 
                        annualConsumption={quote.plant_data?.consumo_annuo_kwh}
                        annualSaving={quote.simulation.annual_saving}
                        currentEnergyPrice={quote.plant_data?.prezzo_energia_kwh || 0.25}
                        annualPriceIncrease={quote.plant_data?.aumento_energia_annuo || 4}
                    />
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Production Details */}
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Sun className="w-4 h-4" />
                                Dettagli Produzione
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm">
                            <div className="flex justify-between py-1.5 border-b">
                                <span className="text-zinc-500">Energia Autoconsumata</span>
                                <span className="font-medium">{quote.simulation.self_consumption_kwh?.toLocaleString()} kWh</span>
                            </div>
                            <div className="flex justify-between py-1.5 border-b">
                                <span className="text-zinc-500">Immessa in Rete</span>
                                <span className="font-medium">{quote.simulation.energy_to_grid_kwh?.toLocaleString()} kWh</span>
                            </div>
                            <div className="flex justify-between py-1.5 border-b">
                                <span className="text-zinc-500">Prelevata dalla Rete</span>
                                <span className="font-medium">{quote.simulation.energy_from_grid_kwh?.toLocaleString()} kWh</span>
                            </div>
                            <div className="flex justify-between py-1.5">
                                <span className="text-zinc-500">Autosufficienza</span>
                                <span className="font-bold text-green-600">{quote.simulation.self_sufficiency_percent}%</span>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Economic Details */}
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center gap-2">
                                <TrendUp className="w-4 h-4" />
                                Dettagli Economici
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm">
                            <div className="flex justify-between py-1.5 border-b">
                                <span className="text-zinc-500">Risparmio 20 anni</span>
                                <span className="font-bold text-blue-600">{formatCurrency(quote.simulation.saving_20_years)}</span>
                            </div>
                            <div className="flex justify-between py-1.5 border-b">
                                <span className="text-zinc-500">ROI Annuo</span>
                                <span className="font-medium">{quote.simulation.annual_roi_percent}%</span>
                            </div>
                            <div className="flex justify-between py-1.5 border-b">
                                <span className="text-zinc-500">Prezzo Energia</span>
                                <span className="font-medium">€{quote.plant_data?.prezzo_energia_kwh || 0.25}/kWh</span>
                            </div>
                            <div className="flex justify-between py-1.5">
                                <span className="text-zinc-500">Aumento Annuo</span>
                                <span className="font-medium">+{quote.plant_data?.aumento_energia_annuo || 4}%</span>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Environmental Impact */}
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Leaf className="w-4 h-4" />
                                Impatto Ambientale
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-3 gap-2 text-center">
                                <div className="p-2 bg-green-50 rounded-lg">
                                    <Leaf className="w-6 h-6 mx-auto text-green-600 mb-1" />
                                    <p className="text-xs text-green-700">CO₂/anno</p>
                                    <p className="text-sm font-bold text-green-900">
                                        {quote.simulation.co2_saving_ton} t
                                    </p>
                                </div>
                                <div className="p-2 bg-emerald-50 rounded-lg">
                                    <TreeEvergreen className="w-6 h-6 mx-auto text-emerald-600 mb-1" />
                                    <p className="text-xs text-emerald-700">Alberi</p>
                                    <p className="text-sm font-bold text-emerald-900">
                                        {quote.simulation.trees_equivalent}
                                    </p>
                                </div>
                                <div className="p-2 bg-teal-50 rounded-lg">
                                    <Sun className="w-6 h-6 mx-auto text-teal-600 mb-1" />
                                    <p className="text-xs text-teal-700">MWh/anno</p>
                                    <p className="text-sm font-bold text-teal-900">
                                        {(quote.simulation.annual_production_kwh / 1000).toFixed(1)}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        ) : (
            <Card>
                <CardContent className="text-center py-12">
                    <Calculator className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
                    <p className="text-zinc-500 mb-4">
                        Nessuna simulazione calcolata. Assicurati di aver inserito i dati impianto (potenza kWp).
                    </p>
                    <Button onClick={onCalculate} disabled={saving} data-testid="calculate-simulation-btn">
                        <Calculator className="w-4 h-4 mr-2" />
                        Calcola Simulazione
                    </Button>
                </CardContent>
            </Card>
        )}
    </TabsContent>
);
